import { Input } from '@billy/design-system'
import { useCallback, useMemo } from 'react'
import { useWatch } from 'react-hook-form'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { useGetZervantDocumentSettings } from '@features/invoicingSettings/hooks/useGetZervantDocumentSettings'
import { VatRateSelect } from '@features/vatRates/elements/VatRateSelect'
import { useFilteredVatRates } from '@features/vatRates/hooks/useFilteredVatRates'
import { useGetRegionFromContactCountryCode } from '@features/vatRates/hooks/useGetRegionFromContactCountryCode'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useInvoiceEditorForm } from '../../../../../../../../../../../../../../contexts/invoiceEditorFormContext'
import { useUpdateDependentLineValues } from '../../../../../../../../../../hooks/useUpdateDependentLineValues'
import { CommonInvoiceLineFieldProps } from '../../../../../../types/commonInvoiceLineFieldProps'

type InvoiceLineVatProps = CommonInvoiceLineFieldProps

export const InvoiceLineVat = ({ disabled = false, lineIndex }: InvoiceLineVatProps) => {
  const { organization } = useUserOrganization()
  const { invoiceEditorForm, trackInvoiceEditor } = useInvoiceEditorForm()
  const { control, FormItem } = invoiceEditorForm
  const { handleVatRateChange } = useUpdateDependentLineValues()
  const { getInvoiceLocale } = useGetZervantDocumentSettings(InvoiceDocumentType.Invoice)
  const { getRegionFromContactCountryCode } = useGetRegionFromContactCountryCode()
  const { isLoading, getFilteredVatRates } = useFilteredVatRates()

  const contactCountryCode = useWatch({ control, name: 'receiver.countryId' })
  const contactType = useWatch({ control, name: 'receiver.type' })
  const productType = useWatch({ control, name: `invoiceLines.${lineIndex}.productType` })

  const vatRates = useMemo(
    () => (isLoading ? [] : getFilteredVatRates({ contactType, productType, contactCountryCode })),
    [contactCountryCode, contactType, getFilteredVatRates, isLoading, productType],
  )
  const region = useMemo(
    () => getRegionFromContactCountryCode(contactCountryCode),
    [contactCountryCode, getRegionFromContactCountryCode],
  )

  const locale = getInvoiceLocale()

  const handleDropdownOpen = useCallback(() => {
    trackInvoiceEditor('Form - Invoice lines - VAT dropdown opened')
  }, [trackInvoiceEditor])

  return (
    <>
      <FormItem
        name={`invoiceLines.${lineIndex}.vatRateId`}
        render={({ field: { value, onChange, ...rest } }) => {
          if (organization?.isVatExempted) {
            return <Input {...rest} hidden value={value} />
          }

          return (
            <VatRateSelect
              {...rest}
              disabled={disabled}
              dropdownFetching={isLoading}
              dropdownSize="m"
              locale={locale}
              onDropdownOpen={handleDropdownOpen}
              onSelect={(_id, vatRate) => {
                handleVatRateChange(lineIndex, vatRate?.id)
                onChange(vatRate?.id)
                trackInvoiceEditor('Form - Invoice lines - VAT rate selected', {
                  customerCountry: contactCountryCode,
                  customerEntityType: contactType,
                  productType,
                  selectedRate: vatRate?.predefinedTag,
                })
              }}
              region={region}
              selectLook={false}
              selectedId={value}
              vatRates={vatRates}
              withSearch={false}
              size="sm"
            />
          )
        }}
      />
      <Input name={`invoiceLines.${lineIndex}.vatRatePercent`} hidden />
    </>
  )
}
